import { Component } from 'react'
import styled from 'styled-components'
import Barcode from 'react-barcode'
import JsBarcode from 'jsbarcode'
import { FaPrint, FaDownload, FaCopy, FaExpand } from 'react-icons/fa'
import swaller from '../Library/Tools/Swaller'
import ContentStyle from './ContentStyle'

const BarcodeDisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.radius.card};
  box-shadow: 0 2px 8px ${props => props.theme.colors.shadow.light};
  border: 1px solid ${props => props.theme.colors.border.medium};
  transition: all 0.3s ease;
`

const BarcodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: ${props => props.theme.colors.background.primary};
  border: 2px dashed ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  position: relative;
  transition: all 0.3s ease;

  &.print-mode {
    border: none;
    padding: 1rem;
    page-break-inside: avoid;
  }
`

const BarcodeInfo = styled.div`
  text-align: center;

  .barcode-value {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.colors.text.secondary};
    letter-spacing: 2px;
    margin-bottom: 0.5rem;
    transition: color 0.3s ease;
  }

  .article-title {
    font-size: 14px;
    color: ${props => props.theme.colors.text.tertiary};
    margin-top: 0.5rem;
    transition: color 0.3s ease;
  }
`

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 2px solid ${({ variant, theme }) => {
    switch (variant) {
      case 'primary': return theme.colors.semantic.info
      case 'success': return theme.colors.semantic.success
      case 'info': return theme.colors.semantic.info
      default: return theme.colors.semantic.secondary
    }
  }};
  background: ${({ variant, theme }) => {
    switch (variant) {
      case 'primary': return theme.colors.semantic.info
      case 'success': return theme.colors.semantic.success
      case 'info': return theme.colors.semantic.info
      default: return theme.colors.semantic.secondary
    }
  }};
  color: ${props => props.theme.colors.text.inverse};
  border-radius: ${props => props.theme.radius.button};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  font-family: ${props => props.theme.typography.fontFamily};
  transition: ${props => props.theme.transitions.button};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${props => props.theme.colors.shadow.medium};
    opacity: 0.9;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 14px;
  }
`

const SizeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`

const SizeLabel = styled(ContentStyle.Label)`
  margin: 0;
  min-width: auto;
`

const SizeSlider = styled.input`
  width: 150px;
  height: 4px;
  border-radius: 2px;
  background: ${props => props.theme.colors.border.medium};
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.colors.semantic.info};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.colors.semantic.info};
    cursor: pointer;
    border: none;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.shadow.overlay};
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${({ show }) => show ? 'visible' : 'hidden'};
  opacity: ${({ show }) => show ? '1' : '0'};
  transition: all 0.3s ease;
`

const ModalContent = styled.div`
  background: ${props => props.theme.colors.background.primary};
  padding: 2rem;
  border-radius: ${props => props.theme.radius.card};
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  transition: background 0.3s ease;
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.theme.colors.semantic.danger};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  border-radius: ${props => props.theme.radius.button};
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitions.default};

  &:hover {
    opacity: 0.9;
    transform: scale(1.05);
  }
`

const EmptyMessage = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.text.tertiary};
  padding: 2rem;
  transition: color 0.3s ease;
`

const CompactContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
`

const CompactBarcodeWrapper = styled.div`
  background: #ffffff;
  padding: 1rem;
  border-radius: ${props => props.theme.radius.input};
  border: 1px solid ${props => props.theme.colors.border.medium};
  display: flex;
  justify-content: center;
  overflow: hidden;
  flex: 1;
`

const CompactActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export default class BarcodeDisplay extends Component {
  constructor (props) {
    super(props)
    this.state = {
      width: 2,
      height: 80,
      fontSize: 14,
      showModal: false
    }
  }

  handlePrint = () => {
    const { barcode, articleTitle } = this.props

    const printWindow = window.open('', '', 'width=600,height=400')
    if (!printWindow) return

    const doc = printWindow.document
    printWindow.document.title = 'Etichetta Barcode'

    const style = doc.createElement('style')
    style.textContent = `
      body { 
        font-family: Arial, sans-serif; 
        text-align: center; 
        padding: 20px; 
        margin: 0;
        background: white;
      }
      .barcode-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 2rem;
        border: 2px solid #000;
        border-radius: 8px;
        margin: 1rem auto;
        max-width: 400px;
      }
      .barcode-value {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 16px;
        font-weight: bold;
        color: #000;
        letter-spacing: 2px;
        margin: 0.5rem 0;
      }
      .article-title {
        font-size: 14px;
        color: #333;
        margin: 0.5rem 0;
      }
      @media print {
        body { margin: 0; padding: 10px; }
        .barcode-container { border: 1px solid #000; margin: 0; }
      }
    `
    doc.head.appendChild(style)

    doc.body.innerHTML = `
      <div class="barcode-container">
        <div class="barcode-value">${barcode}</div>
        <div id="barcode-svg"></div>
        ${articleTitle ? `<div class="article-title">${articleTitle}</div>` : ''}
      </div>
    `

    // Genera il barcode SVG
    const canvas = document.createElement('canvas')
    JsBarcode(canvas, barcode, {
      format: 'EAN13',
      width: this.state.width,
      height: this.state.height,
      fontSize: this.state.fontSize,
      textMargin: 5
    })

    const svgElement = doc.getElementById('barcode-svg')
    const img = doc.createElement('img')
    img.src = canvas.toDataURL()
    svgElement.appendChild(img)

    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 500)
  }

  handleDownload = () => {
    const { barcode } = this.props

    const canvas = document.createElement('canvas')

    JsBarcode(canvas, barcode, {
      format: 'EAN13',
      width: this.state.width,
      height: this.state.height,
      fontSize: this.state.fontSize
    })

    const link = document.createElement('a')
    link.download = `barcode-${barcode}.png`
    link.href = canvas.toDataURL()
    link.click()
  }

  handleCopy = () => {
    const { barcode } = this.props
    navigator.clipboard.writeText(barcode).then(() => {
      swaller.info('Barcode copiato negli appunti!')
    }).catch(() => {
      swaller.error('Errore nella copia del barcode')
    })
  }

  render () {
    const { barcode, articleTitle, compact = false } = this.props
    const { width, height, fontSize, showModal } = this.state

    if (!barcode) {
      return (
        <BarcodeDisplayContainer>
          <EmptyMessage>
            Nessun barcode disponibile
          </EmptyMessage>
        </BarcodeDisplayContainer>
      )
    }

    const BarcodeComponent = (
      <Barcode
        value={barcode}
        width={width}
        height={height}
        fontSize={fontSize}
        background='#ffffff'
        lineColor='#000000'
        textMargin={8}
        margin={10}
      />
    )

    if (compact) {
      return (
        <CompactContainer>
          <CompactBarcodeWrapper>
            <Barcode
              value={barcode}
              width={1.5}
              height={50}
              fontSize={12}
              background='#ffffff'
              lineColor='#000000'
              textMargin={3}
              margin={0}
            />
          </CompactBarcodeWrapper>
          <CompactActions>
            <ActionButton variant='primary' onClick={this.handlePrint}>
              <FaPrint />
            </ActionButton>
            <ActionButton variant='info' onClick={this.handleCopy}>
              <FaCopy />
            </ActionButton>
          </CompactActions>
        </CompactContainer>
      )
    }

    return (
      <>
        <BarcodeDisplayContainer>
          <SizeControls>
            <SizeLabel>Larghezza:</SizeLabel>
            <SizeSlider
              type='range'
              min='1'
              max='4'
              step='0.5'
              value={width}
              onChange={(e) => this.setState({ width: parseFloat(e.target.value) })}
            />
            <span>{width}px</span>

            <SizeLabel>Altezza:</SizeLabel>
            <SizeSlider
              type='range'
              min='40'
              max='120'
              step='10'
              value={height}
              onChange={(e) => this.setState({ height: parseInt(e.target.value) })}
            />
            <span>{height}px</span>

            <SizeLabel>Font:</SizeLabel>
            <SizeSlider
              type='range'
              min='10'
              max='20'
              step='1'
              value={fontSize}
              onChange={(e) => this.setState({ fontSize: parseInt(e.target.value) })}
            />
            <span>{fontSize}px</span>
          </SizeControls>

          <BarcodeWrapper>
            <BarcodeInfo>
              <div className='barcode-value'>{barcode}</div>
              {BarcodeComponent}
              {articleTitle && <div className='article-title'>{articleTitle}</div>}
            </BarcodeInfo>
          </BarcodeWrapper>

          <ActionsRow>
            <ActionButton variant='primary' onClick={this.handlePrint}>
              <FaPrint />
              Stampa
            </ActionButton>
            <ActionButton variant='success' onClick={this.handleDownload}>
              <FaDownload />
              Scarica PNG
            </ActionButton>
            <ActionButton variant='info' onClick={this.handleCopy}>
              <FaCopy />
              Copia
            </ActionButton>
            <ActionButton onClick={() => this.setState({ showModal: true })}>
              <FaExpand />
              Anteprima
            </ActionButton>
          </ActionsRow>
        </BarcodeDisplayContainer>

        <ModalOverlay show={showModal} onClick={() => this.setState({ showModal: false })}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => this.setState({ showModal: false })}>
              ×
            </CloseButton>
            <h3 style={{ textAlign: 'center', marginBottom: '2rem' }}>Anteprima Barcode</h3>
            <BarcodeWrapper>
              <BarcodeInfo>
                <div className='barcode-value'>{barcode}</div>
                <Barcode
                  value={barcode}
                  width={3}
                  height={100}
                  fontSize={16}
                  background='#ffffff'
                  lineColor='#000000'
                  textMargin={8}
                  margin={10}
                />
                {articleTitle && <div className='article-title'>{articleTitle}</div>}
              </BarcodeInfo>
            </BarcodeWrapper>
          </ModalContent>
        </ModalOverlay>
      </>
    )
  }
}
